import { History, Trash2, TrendingDown, TrendingUp } from 'lucide-react'
// Optimized: Incremental Calculation of History via useRef to avoid re-rendering loops
import React, { useEffect, useState } from 'react'
import { CONFIG } from '../config/constants'
import { formatDateToDDMMYYYY, formatISTTime } from '../lib/date-utils'
import { getShiftColorClass } from '../lib/table-utils'
import { cn } from '../lib/utils'
import type { AnalysisMode, AnalysisResult, MarketData } from '../types/index'
import { type ColumnConfig, ColumnSelector } from './ColumnSelector'
import { NumberCell } from './ui/ghost-table/DataCells'
import { StrikeChip } from './ui/ghost-table/StrikeChip'
import { TableWrapper } from './ui/ghost-table/TableWrapper'
import { Pagination } from './ui/Pagination'
import { WidgetErrorBoundary } from './ui/WidgetErrorBoundary'

interface HistoricalStrike {
    timestamp: string
    resistanceStrike: number
    supportStrike: number
    resistanceLtp: number
    supportLtp: number
    shift: number
    mode: AnalysisMode
}

interface HistoricalStrikesTableProps {
    currentAnalysis: AnalysisResult | null
    marketData: MarketData | null
    mode: AnalysisMode
    settings: any
}

export const LiveStrikeChangeTracker: React.FC<HistoricalStrikesTableProps> = (props) => (
    <WidgetErrorBoundary fallbackDescription="Live Strike Tracker">
        <HistoricalStrikesTable {...props} />
    </WidgetErrorBoundary>
)

const HistoricalStrikesTable: React.FC<HistoricalStrikesTableProps> = ({
    currentAnalysis,
    marketData,
    mode,
    settings,
}) => {
    const [history, setHistory] = useState<HistoricalStrike[]>([])
    const [hasChanges, setHasChanges] = useState(false)

    // Pagination
    const [currentPage, setCurrentPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(5)

    const [columns, setColumns] = useState<ColumnConfig[]>([
        { id: 'resistance', label: 'Resistance Strike', visible: true },
        { id: 'res_ltp', label: 'Res. LTP', visible: true },
        { id: 'support', label: 'Support Strike', visible: true },
        { id: 'sup_ltp', label: 'Sup. LTP', visible: true },
        { id: 'shift', label: 'Shift', visible: true },
        { id: 'status', label: 'Status', visible: true },
    ])

    // Hydrate Settings
    useEffect(() => {
        if (typeof window !== 'undefined') {
            try {
                const savedRows = localStorage.getItem(CONFIG.UI.STORAGE_KEYS.LIVE_STRIKE_ROWS)
                if (savedRows) setRowsPerPage(parseInt(savedRows, 10))

                const savedCols = localStorage.getItem(CONFIG.UI.STORAGE_KEYS.LIVE_STRIKE_COLUMNS)
                if (savedCols) setColumns(JSON.parse(savedCols))
            } catch (e) { console.warn('Ignored error in LiveStrikeChangeTracker.tsx:', e); }
        }
    }, [])

    useEffect(() => {
        localStorage.setItem(CONFIG.UI.STORAGE_KEYS.LIVE_STRIKE_COLUMNS, JSON.stringify(columns))
    }, [columns])

    useEffect(() => {
        localStorage.setItem(CONFIG.UI.STORAGE_KEYS.LIVE_STRIKE_ROWS, rowsPerPage.toString())
    }, [rowsPerPage])

    const toggleColumn = (id: string) => {
        setColumns((prev) => prev.map((col) => (col.id === id ? { ...col, visible: !col.visible } : col)))
    }

    const isVisible = (id: string) => columns.find((c) => c.id === id)?.visible

    const calculatedHistoryRef = React.useRef<HistoricalStrike[]>([])
    const prevAnalysisRef = React.useRef<{
        resistanceStrike: number
        supportStrike: number
        shift: number
    } | null>(null)
    const lastSymbolRef = React.useRef<string>(settings.symbol)
    const lastExpiryRef = React.useRef<string | null>(settings.expiry)
    const lastModeRef = React.useRef<AnalysisMode>(mode)

    useEffect(() => {
        if (!currentAnalysis || !marketData) return

        if (
            settings.symbol !== lastSymbolRef.current ||
            settings.expiry !== lastExpiryRef.current ||
            mode !== lastModeRef.current
        ) {
            setHistory([])
            calculatedHistoryRef.current = []
            prevAnalysisRef.current = null
            lastSymbolRef.current = settings.symbol
            lastExpiryRef.current = settings.expiry
            lastModeRef.current = mode
        }

        const prev = prevAnalysisRef.current
        const resistanceChanged = !prev || prev.resistanceStrike !== currentAnalysis.resistanceStrike
        const supportChanged = !prev || prev.supportStrike !== currentAnalysis.supportStrike
        const shiftChanged = !prev || Math.abs(prev.shift - currentAnalysis.shift) > 0.1

        if (
            resistanceChanged ||
            supportChanged ||
            shiftChanged ||
            (calculatedHistoryRef.current.length === 0 && history.length === 0)
        ) {
            const updatedTime = marketData.updatedAt ? new Date(marketData.updatedAt) : new Date()
            const ts = formatISTTime(updatedTime)
            const date = formatDateToDDMMYYYY(updatedTime)
            const fullTs = `${date} ${ts}`

            const newItem: HistoricalStrike = {
                timestamp: fullTs,
                resistanceStrike: currentAnalysis.resistanceStrike,
                supportStrike: currentAnalysis.supportStrike,
                resistanceLtp: 0,
                supportLtp: 0,
                shift: currentAnalysis.shift,
                mode,
            }

            // Lookup LTPs
            if (marketData.options) {
                const rOpt = marketData.options.find((o) => o.strike === currentAnalysis.resistanceStrike)
                const sOpt = marketData.options.find((o) => o.strike === currentAnalysis.supportStrike)
                if (rOpt) newItem.resistanceLtp = rOpt.callPrice
                if (sOpt) newItem.supportLtp = sOpt.putPrice
            }

            const newHist = [newItem, ...calculatedHistoryRef.current].slice(0, 50)
            calculatedHistoryRef.current = newHist
            setHistory(newHist)
            setHasChanges(true)

            prevAnalysisRef.current = {
                resistanceStrike: currentAnalysis.resistanceStrike,
                supportStrike: currentAnalysis.supportStrike,
                shift: currentAnalysis.shift,
            }
        }
    }, [currentAnalysis, marketData, settings.symbol, settings.expiry, mode, history.length])

    const clearHistory = () => {
        setHistory([])
        setHasChanges(false)
    }

    const totalPages = Math.ceil(history.length / rowsPerPage)
    const paginatedHistory = history.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)

    if (!hasChanges && history.length === 0) {
        return null
    }

    const headerClass =
        'p-3 text-[10px] font-bold uppercase tracking-wider bg-white/50 dark:bg-slate-800/50 border-b border-slate-100/50 dark:border-slate-800/50 text-slate-500 dark:text-slate-400 cursor-pointer hover:bg-white dark:hover:bg-slate-800 transition-colors duration-200'

    return (
        <div className="animate-in slide-in-from-bottom-4 fade-in duration-500">
            <TableWrapper className="shadow-xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 table-wrapper">
                {/* Clean Table Header */}
                <div className="px-6 py-5 flex flex-wrap gap-y-4 justify-between items-center border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-t-[20px]">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-xl text-amber-600 dark:text-amber-500 shadow-sm ring-1 ring-amber-100 dark:ring-amber-900/30">
                            <History size={22} strokeWidth={2.5} />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-slate-800 dark:text-slate-200 tracking-tight">
                                Historical Strike Changes
                            </h2>
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-medium">
                                Tracking movements in Support & Resistance • {history.length} Records
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                            <span className="text-xs font-medium text-slate-400">Rows:</span>
                            <input
                                type="number"
                                min={1}
                                max={100}
                                value={rowsPerPage}
                                onChange={(e) => {
                                    const val = Math.min(100, Math.max(1, Number(e.target.value) || 1));
                                    setRowsPerPage(val);
                                }}
                                className="w-12 h-9 px-1 text-center text-xs font-bold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 shadow-sm transition-all"
                            />
                        </div>
                        <ColumnSelector columns={columns} onChange={toggleColumn} />
                        {history.length > 0 && (
                            <button
                                onClick={clearHistory}
                                className="text-xs font-bold text-slate-400 hover:text-rose-500 flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-all border border-slate-200 dark:border-slate-700 hover:border-rose-100 cursor-pointer"
                            >
                                <Trash2 size={14} strokeWidth={2.5} /> Clear
                            </button>
                        )}
                    </div>
                </div>

                <div className="w-full relative overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead className="sticky top-0 z-30 shadow-sm">
                            <tr>
                                <th className={cn(headerClass, 'text-left pl-6')}>Time Log</th>
                                {isVisible('resistance') && (
                                    <th className={cn(headerClass, 'text-center')}>Resistance Strike</th>
                                )}
                                {isVisible('res_ltp') && <th className={cn(headerClass, 'text-right')}>Res. LTP</th>}
                                {isVisible('support') && (
                                    <th className={cn(headerClass, 'text-center')}>Support Strike</th>
                                )}
                                {isVisible('sup_ltp') && <th className={cn(headerClass, 'text-right')}>Sup. LTP</th>}
                                {isVisible('shift') && <th className={cn(headerClass, 'text-center')}>Shift</th>}
                                {isVisible('status') && <th className={cn(headerClass, 'text-right pr-6')}>Status</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100/50 dark:divide-slate-800/50 bg-white/30 dark:bg-slate-900/30">
                            {paginatedHistory.map((entry, index) => {
                                const actualIndex = (currentPage - 1) * rowsPerPage + index
                                const prevEntry = history[actualIndex + 1]

                                const resistanceChange = prevEntry
                                    ? entry.resistanceStrike - prevEntry.resistanceStrike
                                    : 0
                                const supportChange = prevEntry ? entry.supportStrike - prevEntry.supportStrike : 0
                                const shiftChange = prevEntry ? entry.shift - prevEntry.shift : 0
                                const isLatest = actualIndex === 0

                                const tdClass =
                                    'p-3 align-middle transition-colors border-b border-slate-100/50 dark:border-slate-800/50'

                                return (
                                    <tr
                                        key={`${entry.timestamp}-${index}`}
                                        className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors"
                                    >
                                        <td className={cn(tdClass, 'pl-6')}>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-black text-slate-700 dark:text-slate-300 premium-cell-mono">
                                                    {entry.timestamp.includes('T')
                                                        ? (entry.timestamp.split('T')[1] || '').split('.')[0] || ''
                                                        : entry.timestamp.split(' ')[1] || entry.timestamp}
                                                </span>
                                                <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-tight mt-0.5 premium-cell-mono">
                                                    {entry.timestamp.includes('T')
                                                        ? entry.timestamp.split('T')[0] || ''
                                                        : entry.timestamp.split(' ')[0] || ''}
                                                </span>
                                            </div>
                                        </td>

                                        {isVisible('resistance') && (
                                            <td className={cn(tdClass, 'text-center')}>
                                                <div className="flex flex-col items-center gap-1">
                                                    <StrikeChip
                                                        strike={entry.resistanceStrike}
                                                        isAtm={false}
                                                        label="R"
                                                    />
                                                    {resistanceChange !== 0 && (
                                                        <span
                                                            className={cn(
                                                                'text-[10px] font-bold flex items-center gap-1 premium-cell-mono',
                                                                resistanceChange > 0
                                                                    ? 'text-emerald-600'
                                                                    : 'text-rose-600',
                                                            )}
                                                        >
                                                            {resistanceChange > 0 ? (
                                                                <TrendingUp size={12} />
                                                            ) : (
                                                                <TrendingDown size={12} />
                                                            )}
                                                            {Math.abs(resistanceChange)}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        )}

                                        {isVisible('res_ltp') && (
                                            <td className={cn(tdClass, 'text-right')}>
                                                <NumberCell
                                                    value={entry.resistanceLtp?.toFixed(2) || '-'}
                                                    className="text-slate-600"
                                                />
                                            </td>
                                        )}

                                        {isVisible('support') && (
                                            <td className={cn(tdClass, 'text-center')}>
                                                <div className="flex flex-col items-center gap-1">
                                                    <StrikeChip strike={entry.supportStrike} isAtm={false} label="S" />
                                                    {supportChange !== 0 && (
                                                        <span
                                                            className={cn(
                                                                'text-[10px] font-bold flex items-center gap-1 premium-cell-mono',
                                                                supportChange > 0
                                                                    ? 'text-emerald-600'
                                                                    : 'text-rose-600',
                                                            )}
                                                        >
                                                            {supportChange > 0 ? (
                                                                <TrendingUp size={12} />
                                                            ) : (
                                                                <TrendingDown size={12} />
                                                            )}
                                                            {Math.abs(supportChange)}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        )}

                                        {isVisible('sup_ltp') && (
                                            <td className={cn(tdClass, 'text-right')}>
                                                <NumberCell
                                                    value={entry.supportLtp?.toFixed(2) || '-'}
                                                    className="text-slate-600"
                                                />
                                            </td>
                                        )}

                                        {isVisible('shift') && (
                                            <td className={cn(tdClass, 'text-center')}>
                                                <span
                                                    className={cn(
                                                        'premium-cell-mono text-sm font-black px-3 py-1.5 rounded-xl shadow-sm border inline-block',
                                                        getShiftColorClass(shiftChange),
                                                    )}
                                                >
                                                    {Number.isNaN(entry.shift)
                                                        ? '-'
                                                        : Math.round(Math.abs(entry.shift))}
                                                </span>
                                            </td>
                                        )}

                                        {isVisible('status') && (
                                            <td className={cn(tdClass, 'text-right pr-6')}>
                                                {isLatest ? (
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black bg-emerald-50 text-emerald-700 border border-emerald-100 shadow-sm animate-pulse-slow">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                                                        ACTIVE
                                                    </span>
                                                ) : resistanceChange !== 0 || supportChange !== 0 ? (
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black bg-amber-50 text-amber-700 border border-amber-100 shadow-sm">
                                                        <History size={12} />
                                                        CHANGED
                                                    </span>
                                                ) : (
                                                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                                                        No Change
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                )
                            })}
                            {history.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="py-12 text-center text-slate-400 italic font-medium">
                                        No historical changes recorded yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        rowsPerPage={rowsPerPage}
                        onPageChange={setCurrentPage}
                        onRowsPerPageChange={(rows) => {
                            setRowsPerPage(rows)
                            setCurrentPage(1)
                        }}
                        totalItems={history.length}
                        className="p-4"
                    />
                )}
            </TableWrapper>
        </div>
    )
}
